// Drawola Studio — "Flowchart" built-in template. Fork-only.
//
// Authored as an `ExcalidrawElementSkeleton[]` and handed to upstream's public
// `convertToExcalidrawElements`, so seeds / versions / nonces / fractional
// indices and the arrow<->shape bindings are all produced by the library
// rather than written out by hand.

import { ROUNDNESS } from "@excalidraw/common";
import { convertToExcalidrawElements } from "@excalidraw/excalidraw";
import { pointFrom } from "@excalidraw/math";

import type { ExcalidrawElementSkeleton } from "@excalidraw/element/transform";
import type { LocalPoint } from "@excalidraw/math";

import { FILL, STROKE, templateAppState } from "./shared";

import type { TemplateScene } from "./types";

const ROUNDED = { type: ROUNDNESS.ADAPTIVE_RADIUS } as const;

// column centre for the happy path; the error branch hangs off to the right
const MAIN_X = 360;

export const buildFlowchartTemplate = (): TemplateScene => {
  const skeleton: ExcalidrawElementSkeleton[] = [
    // ---- happy path, top to bottom ------------------------------------
    {
      type: "ellipse",
      id: "start",
      x: MAIN_X - 80,
      y: 100,
      width: 160,
      height: 64,
      backgroundColor: FILL.GREEN,
      label: { text: "Start", fontSize: 20 },
    },
    {
      type: "rectangle",
      id: "collect",
      x: MAIN_X - 100,
      y: 204,
      width: 200,
      height: 72,
      backgroundColor: FILL.BLUE,
      roundness: ROUNDED,
      label: { text: "Collect input", fontSize: 16 },
    },
    {
      type: "diamond",
      id: "valid",
      x: MAIN_X - 110,
      y: 316,
      width: 220,
      height: 130,
      backgroundColor: FILL.YELLOW,
      label: { text: "Valid?", fontSize: 16 },
    },
    {
      type: "rectangle",
      id: "save",
      x: MAIN_X - 100,
      y: 506,
      width: 200,
      height: 72,
      backgroundColor: FILL.BLUE,
      roundness: ROUNDED,
      label: { text: "Save record", fontSize: 16 },
    },
    {
      type: "ellipse",
      id: "done",
      x: MAIN_X - 80,
      y: 618,
      width: 160,
      height: 64,
      backgroundColor: FILL.GREEN,
      label: { text: "Done", fontSize: 20 },
    },

    // ---- error branch --------------------------------------------------
    {
      type: "rectangle",
      id: "error",
      x: 560,
      y: 345,
      width: 190,
      height: 72,
      backgroundColor: FILL.RED,
      roundness: ROUNDED,
      label: { text: "Show error", fontSize: 16 },
    },

    // ---- arrows ---------------------------------------------------------
    // Bound at both ends (`start` / `end` by skeleton id), so dragging a shape
    // in the editor drags its arrows along.
    {
      type: "arrow",
      x: MAIN_X,
      y: 164,
      points: [pointFrom<LocalPoint>(0, 0), pointFrom<LocalPoint>(0, 40)],
      start: { id: "start" },
      end: { id: "collect" },
    },
    {
      type: "arrow",
      x: MAIN_X,
      y: 276,
      points: [pointFrom<LocalPoint>(0, 0), pointFrom<LocalPoint>(0, 40)],
      start: { id: "collect" },
      end: { id: "valid" },
    },
    {
      type: "arrow",
      x: MAIN_X,
      y: 446,
      points: [pointFrom<LocalPoint>(0, 0), pointFrom<LocalPoint>(0, 60)],
      start: { id: "valid" },
      end: { id: "save" },
      label: { text: "Yes", fontSize: 16 },
    },
    {
      type: "arrow",
      x: MAIN_X,
      y: 578,
      points: [pointFrom<LocalPoint>(0, 0), pointFrom<LocalPoint>(0, 40)],
      start: { id: "save" },
      end: { id: "done" },
    },
    {
      type: "arrow",
      x: MAIN_X + 110,
      y: 381,
      points: [pointFrom<LocalPoint>(0, 0), pointFrom<LocalPoint>(90, 0)],
      start: { id: "valid" },
      end: { id: "error" },
      label: { text: "No", fontSize: 16 },
    },
    // retry loop: up out of "Show error", then left back into "Collect input"
    {
      type: "arrow",
      x: 655,
      y: 345,
      points: [
        pointFrom<LocalPoint>(0, 0),
        pointFrom<LocalPoint>(0, -105),
        pointFrom<LocalPoint>(-195, -105),
      ],
      strokeColor: STROKE.MUTED,
      start: { id: "error" },
      end: { id: "collect" },
    },
  ];

  return {
    elements: convertToExcalidrawElements(skeleton),
    appState: templateAppState(),
  };
};
